import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {mcpAccountLinkConfig as config} from './mcp-embed.config';
declare const Stripe: any;
/** Browser-only checkout relay. Never reads or replaces the ordinary web login. */
@Injectable({providedIn:'root'})
export class McpBillingService {
 private original=new URL(location.href);
 private key='mango-mcp-billing-return';
 private pending:any=this.readPending();
 private enabled=config.enabled && window.parent===window && location.origin===environment.webAppUrl;
 readonly active=this.enabled && (new URLSearchParams(this.original.hash.slice(1)).has('mcpBilling') || this.original.hash==='#mcpBillingResult' || !!this.pending && ['/','/payment-success','/payment-error','/plans-and-payments','/user-profile','/mirrors'].includes(this.original.pathname));
 title='Payments and Plans';
 status='Opening Payments and Plans…';
 requiresPassword=false;
 accountLabel='';
 verifying=false;
 private authRef='';
 // Management alone requires reauthentication; first-time checkout keeps the
 // existing signup flow. The password is submitted to the configured MCP relay,
 // never to a model/tool argument or browser storage. The server validates it
 // using existing Mango login and creates the portal session after verification.
 // This is a chat-entry gate, not a new global backend billing access policy.
 async verifyPassword(password:string):Promise<void>{
  if(this.verifying||!this.requiresPassword||!password)return;
  this.verifying=true;this.status='Verifying your identity…';
  try{
   const response=await fetch(config.mcpOrigin+'/billing/verify',{method:'POST',credentials:'omit',redirect:'error',cache:'no-store',headers:{'Content-Type':'application/json'},body:JSON.stringify({authRef:this.authRef,password})});
   const result=await response.json();
   if(!response.ok){
    this.status=response.status===401?'We could not verify your password. Please try again, or reset your password in the Mango web app.':response.status===429?'Too many attempts. Wait five minutes, then reopen Payments and Plans from chat.':'Verification could not finish. Close this tab and reopen Payments and Plans from chat.';
    if(response.status!==401){this.requiresPassword=false;this.finished=true;}
    return;
   }
   this.rememberReceipt(result.receipt,'management');
   this.requiresPassword=false;this.authRef='';this.status='Opening Stripe subscription management…';
   this.openPortal(result.portalUrl);
  }catch{this.status='Unable to finish verification. Close this tab and reopen Payments and Plans from chat.';this.requiresPassword=false;this.finished=true;}
  finally{this.verifying=false;}
 }
 private rememberReceipt(receipt:string,kind='checkout'){
  if(!/^[A-Za-z0-9_-]{43}$/.test(receipt))throw Error();
  sessionStorage.setItem(this.key,JSON.stringify({receipt,kind,expires:Date.now()+3600000}));
 }
 private openPortal(url:string){const u=new URL(url);if(u.protocol!=='https:'||u.hostname!=='billing.stripe.com'||u.port||u.username||u.password)throw Error();location.assign(u.href);}

 finished=false;
 checking=false;
 canRetry=false;
 private readPending(){try{const r=JSON.parse(sessionStorage.getItem(this.key)||'null');return r?.expires>Date.now()?r:null;}catch{return null;}}
 private async post(path:string,body:any){const r=await fetch(config.mcpOrigin+path,{method:'POST',credentials:'omit',redirect:'error',cache:'no-store',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)});if(!r.ok)throw Error();return r.json();}
 async open():Promise<boolean>{
  if(!this.active)return false;
  history.replaceState(null,'','/#mcpBillingResult');
  try {
   const ticket=new URLSearchParams(this.original.hash.slice(1)).get('mcpBilling');
   if(ticket){
    if(!/^[A-Za-z0-9_-]{43}$/.test(ticket)||!String(environment.stripeKey).startsWith('pk_test_'))throw Error();
    const r=await this.post('/billing/begin',{ticket});
    if(r.requiresPassword===true){
     if(!/^[A-Za-z0-9_-]{43}$/.test(r.authRef)||typeof r.accountLabel!=='string')throw Error();
     this.authRef=r.authRef;this.accountLabel=r.accountLabel;this.requiresPassword=true;
     this.status='For your security, re-enter your Mango password to manage your subscription and payment details.';
     return true;
    }
    this.rememberReceipt(r.receipt);
    this.status='Opening secure Stripe checkout for '+r.accountLabel+'…';
    if(typeof r.sessionId==='string'&&/^cs_test_[A-Za-z0-9]+$/.test(r.sessionId)){
     const result=await Stripe(environment.stripeKey).redirectToCheckout({sessionId:r.sessionId});if(result?.error)throw Error();
    }else{const u=new URL(r.portalUrl);if(u.protocol!=='https:'||u.hostname!=='billing.stripe.com'||u.port||u.username||u.password)throw Error();location.assign(u.href);}
    return true;
   }
   if(!this.pending)throw Error();
   // Returning from Stripe does not prove a change: users can view details,
   // schedule cancellation, or leave without editing. Management stays neutral;
   // only new checkout runs the subscription/trial confirmation loop.
   if(this.pending.kind==='management'){
    this.showManagementReturn();
    void this.post('/billing/status',{receipt:this.pending.receipt}).catch(()=>{});
    sessionStorage.removeItem(this.key);this.pending=null;
   }else await this.checkSubscription();
  }catch{this.status='Checkout could not be confirmed here. Return to chat and open Payments and Plans to check before trying again.';}
  this.finished=true;return true;
 }
 private showManagementReturn(){
  this.title='Return to chat';this.status='You can close this tab and continue your conversation.';
  this.checking=false;this.canRetry=false;this.finished=true;
 }
 // Bounded polling handles delayed backend/Stripe updates. A timeout is pending,
 // not proof of failure; do not restart checkout and risk a second submission.
 async checkSubscription():Promise<void>{
  if(this.checking || !this.pending)return;
  this.checking=true;this.finished=false;this.canRetry=false;
  this.status='Confirming your subscription… This may take a moment.';
  try {
   for(let attempt=0;attempt<10;attempt++){
    try {
     const result=await this.post('/billing/status',{receipt:this.pending.receipt});
     if(result.kind==='management'){this.showManagementReturn();sessionStorage.removeItem(this.key);this.pending=null;return;}
     if(result.subscriptionAvailable===true){
      const trial=result.status==='trialing'||result.status==='trialling';
      this.status=trial
       ? 'Your '+(result.planName?result.planName+' ':'')+'free trial has started for '+result.accountLabel+'. Return to chat to continue.'
       : 'Your '+(result.planName?result.planName+' ':'')+'subscription is active for '+result.accountLabel+'. Return to chat to continue.';
      sessionStorage.removeItem(this.key);
      this.pending=null;
      return;
     }
    }catch{ /* A delayed or temporarily unavailable result is not a failed checkout. */ }
    if(attempt<9)await new Promise<void>(resolve=>setTimeout(resolve,3000));
   }
   this.status='We’re still waiting for Mango to confirm your subscription. You can check again or return to chat. Please don’t repeat checkout.';
   this.canRetry=true;
  }finally{this.checking=false;this.finished=true;}
 }
}
